import React, {
  useState,
  FunctionComponent,
  useContext,
  useEffect,
} from "react";
import {
  styled,
  Block,
  Modal,
  PanelHeader,
  IModalProps,
  Panel,
  Checkbox,
  Icon,
} from "../../../seenspire-library";
import PanelForm from "../../components/PanelForm/PanelForm";
import { Formik, FormikHelpers, Field } from "formik";
import * as Yup from "yup";
import { Invite, Invite2, Role } from "seenspire-api-client";
import { formatServerValidationErrors, isClient } from "../../util";
import InputField from "../../components/InputField/InputField";
import { AppContext } from "../../state";
import { useTranslation } from "react-i18next";
import {
  ModalButtons,
  FormModalButtons,
} from "../../components/ModalButtons/ModalButtons";
import {
  onSuccess,
  onFail,
  formStatus,
} from "../../components/FormCtaButton/FormCtaButton";
import { UserRole } from "../EditUserModal/EditUserModal";
import { find, isEmpty } from "lodash";
import { UserRolesEnum } from "../../interfaces";
import { WarningPanel } from "../../containers/Auth/Login/Login";

const Title = styled(Block)`
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  margin-top: 10px;
  text-transform: uppercase;
`;

const InviteUserModal: FunctionComponent<IModalProps & { users: any[] }> = ({
  onSubmit,
  onCancel,
  users,
  ...props
}) => {
  const { slug, client, account, theme } = useContext(AppContext);
  const { t } = useTranslation();
  const [invite, setInvite] = useState<Invite2>({
    email: "",
    role: [{ id: 0 }],
  });
  const [roles, setRoles] = useState<UserRole[]>();

  useEffect(() => {
    client.Users.getRoles().then((d: any) =>
      setRoles(
        d.data
          .filter(
            (role: any) =>
              role.type === "General" ||
              (!isClient(account.account) && role.type === "Partner")
          )
          .map((role: any) => ({
            ...role,
            selected: role.slug === UserRolesEnum.ROLE_OWNER,
          }))
      )
    );
  }, []);

  const submitInviteUser = async (
    values: Invite2,
    actions: FormikHelpers<Invite2>
  ) => {
    if (find(users, (user) => user.email === values.email)) {
      actions.setFieldError(
        "email",
        "This email is already part of this account"
      );
      actions.setSubmitting(false);
      return;
    }

    if (!roles || isEmpty(roles) || isEmpty(roles.filter((r) => r.selected))) {
      actions.setStatus({
        status: formStatus.failed,
        error: "Please select a role",
      });
      actions.setSubmitting(false);
      return;
    }

    const role = roles.filter((r) => r.selected).map((r) => ({ id: r.id }));

    actions.setStatus({});

    try {
      await client.Invites.postInvite({ ...values, role }, slug);
      setInvite({ email: "", role });
      onSubmit();
    } catch (error) {
      onFail(actions, error);
    }
  };

  const selectRole = (role: Role) => {
    if (!roles) return;
    const _roles = roles.map((r) => {
      if (r.id === role.id) {
        r.selected = true;
      } else if (r.slug !== UserRolesEnum.ROLE_PARTNER_PORTAL_MANAGER) {
        r.selected = false;
      }
      return r;
    });
    setRoles(_roles);
  };
  // @TODO Unify with the logic from registration
  const validationSchema = Yup.object().shape({
    email: Yup.string().email(t("auth.email_is_invalid")).required(""),
  });
  if (!account || !roles) return null;
  return (
    <Modal onRequestClose={() => onCancel()} theme={theme} {...props}>
      <Formik
        initialValues={invite}
        validationSchema={validationSchema}
        onSubmit={submitInviteUser}
        enableReinitialize
      >
        {(form) => (
          <>
            <PanelForm form={form} hideHeader disable={!invite}>
              <PanelHeader
                title={t("accounts.invite_user_by_email")}
                textTransform="uppercase"
              />
              <Field
                name="email"
                type="email"
                title={t("auth.work_email")}
                component={InputField}
              />

              <PanelHeader padding={{ top: 12 }} title={t("Account roles")} />
              <Block display="grid" gridGap={5}>
                {roles
                  .filter(
                    (role) =>
                      role.slug !== UserRolesEnum.ROLE_PARTNER_PORTAL_MANAGER
                  )
                  .map((role, index) => (
                    <Block
                      key={index}
                      display="grid"
                      justifySelf="start"
                      direction="column"
                      gridGap={5}
                      cursor="pointer"
                      onClick={() => selectRole(role)}
                    >
                      <Icon
                        margin={{ left: -2 }}
                        justifySelf="start"
                        color={role.selected ? "cta" : "gray"}
                        name={role.selected ? "radioOn" : "radioOff"}
                      />
                      <Block
                        display="grid"
                        justifySelf="start"
                        alignItems="center"
                        color="gray"
                        shade="darkest"
                      >
                        {role.name}{" "}
                      </Block>
                    </Block>
                  ))}
              </Block>

              {!isClient(account.account) && (
                <>
                  <Title>{t("accounts.partner_portal_access_label")}</Title>
                  <Checkbox
                    title="Partner Portal Manager"
                    fontWeight="regular"
                    checked={
                      !!find(
                        roles,
                        (r) =>
                          r.selected &&
                          r.slug === UserRolesEnum.ROLE_PARTNER_PORTAL_MANAGER
                      )
                    }
                    onChange={() => {
                      setRoles([
                        ...roles.map((role) => {
                          if (
                            role.slug ===
                            UserRolesEnum.ROLE_PARTNER_PORTAL_MANAGER
                          ) {
                            role.selected = !role.selected;
                          }
                          return role;
                        }),
                      ]);
                    }}
                  />
                </>
              )}
            </PanelForm>
            {form.status && form.status.status === formStatus.failed && (
              <WarningPanel>
                {form.status.error || t("generals.error_title")}
              </WarningPanel>
            )}
            <FormModalButtons
              outsideForm
              form={form}
              onCancel={onCancel}
              submit={t("Send invite")}
              cancel={t("Cancel")}
            />
          </>
        )}
      </Formik>
    </Modal>
  );
};

export { InviteUserModal };
